import { useState } from 'react';
import { BedDouble, Building2, CalendarClock, ChartLine, DollarSign, Hotel } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';

/** Planning assumptions for the illustrative rebate. Not a Lucid guarantee. */
const ROOMS_PER_TEAM = 12;
const NIGHTS = 2;
const REBATE_MIN = 5;
const REBATE_MAX = 15;

function money(dollars: number): string {
  return dollars.toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });
}

export function RevenueCalculator() {
  const [teamCount, setTeamCount] = useState(24);
  const [feeDollars, setFeeDollars] = useState(650);
  const [outOfTownTeams, setOutOfTownTeams] = useState(12);
  const [rebatePerRoomNight, setRebatePerRoomNight] = useState(REBATE_MIN);

  const boundedOutOfTown = Math.min(outOfTownTeams, teamCount);
  const registrationCollected = teamCount * feeDollars;
  const lodgingEstimate = boundedOutOfTown * ROOMS_PER_TEAM * NIGHTS * rebatePerRoomNight;

  return (
    <section className="bg-slate-950 py-16 md:py-24" data-testid="revenue-calculator">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-10 max-w-3xl text-center">
          <h2 className="font-broadcast text-3xl uppercase tracking-wide text-white md:text-5xl">
            Your upfront software cost
          </h2>
          <p className="mt-3 text-white/70">
            Model a weekend with no license fee and no setup invoice. You set the registration
            amount.
          </p>
        </div>

        <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-2">
          <Card className="border-white/10 bg-white/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <DollarSign className="h-5 w-5 text-emerald-300" aria-hidden />
                Registration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-5">
                <p className="text-xs font-broadcast uppercase tracking-[0.18em] text-emerald-200">
                  License
                </p>
                <p className="mt-1 font-broadcast text-3xl text-white md:text-4xl">
                  Your upfront software cost: $0.00
                </p>
                <p className="mt-3 text-sm text-white/75">
                  No license and no setup invoice. Dugout Desk is covered when teams register.
                </p>
              </div>

              <div>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <Label htmlFor="teams-slider" className="text-white">
                    Number of teams
                  </Label>
                  <span className="font-broadcast text-lg text-white">{teamCount}</span>
                </div>
                <Slider
                  id="teams-slider"
                  min={4}
                  max={80}
                  step={1}
                  value={[teamCount]}
                  onValueChange={value => {
                    const next = value[0] ?? teamCount;
                    setTeamCount(next);
                    setOutOfTownTeams(current => Math.min(current, next));
                  }}
                  aria-label="Number of teams"
                />
              </div>

              <div>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <Label htmlFor="fee-slider" className="text-white">
                    Registration fee
                  </Label>
                  <span className="font-broadcast text-lg text-white">{money(feeDollars)}</span>
                </div>
                <Slider
                  id="fee-slider"
                  min={100}
                  max={1500}
                  step={25}
                  value={[feeDollars]}
                  onValueChange={value => setFeeDollars(value[0] ?? feeDollars)}
                  aria-label="Registration fee"
                />
              </div>

              <p className="text-sm text-white/75">
                Across {teamCount} teams at {money(feeDollars)}, registration is{' '}
                {money(registrationCollected)}.
              </p>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-white/5">
            <CardHeader>
              <CardTitle className="text-white">
                Powered by Lucid Travel — hotel inventory for your event
              </CardTitle>
              <p className="text-sm text-white/60">
                Directors set the rebate. Teams book instant rates or a contracted block, and the
                organization still earns on either path.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-white/10 bg-[#020617] p-4">
                  <div className="mb-2 flex items-center gap-2 text-emerald-300">
                    <BedDouble className="h-5 w-5" aria-hidden />
                    <p className="font-broadcast text-xs uppercase tracking-[0.16em]">
                      Instant inventory
                    </p>
                  </div>
                  <p className="text-sm font-medium text-white">1–9 rooms</p>
                  <p className="mt-2 text-sm leading-relaxed text-white/70">
                    Rates from Booking.com, Expedia, and Hotels.com, hotel-direct GDS, and wholesale
                    rates that are not on public sites (20%+ off). No contracted block is required,
                    including after a block sells out.
                  </p>
                </div>
                <div className="rounded-xl border border-white/10 bg-[#020617] p-4">
                  <div className="mb-2 flex items-center gap-2 text-championship-gold">
                    <Building2 className="h-5 w-5" aria-hidden />
                    <p className="font-broadcast text-xs uppercase tracking-[0.16em]">
                      Room blocks
                    </p>
                  </div>
                  <p className="text-sm font-medium text-white">10+ rooms</p>
                  <p className="mt-2 text-sm leading-relaxed text-white/70">
                    One request. Lucid’s event team texts within 24 hours, negotiates the rate,
                    reward points, and free rooms, then loads the block onto your event travel page.
                  </p>
                </div>
              </div>

              <div>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <Label htmlFor="travel-slider" className="text-white">
                    Out-of-town teams
                  </Label>
                  <span className="font-broadcast text-lg text-white">{boundedOutOfTown}</span>
                </div>
                <Slider
                  id="travel-slider"
                  min={0}
                  max={Math.max(teamCount, 1)}
                  step={1}
                  value={[boundedOutOfTown]}
                  onValueChange={value => setOutOfTownTeams(value[0] ?? boundedOutOfTown)}
                  aria-label="Out-of-town teams"
                />
              </div>

              <div>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <Label htmlFor="rebate-slider" className="text-white">
                    Rebate per room night
                  </Label>
                  <span className="font-broadcast text-lg text-white">
                    {money(rebatePerRoomNight)}
                  </span>
                </div>
                <Slider
                  id="rebate-slider"
                  min={REBATE_MIN}
                  max={REBATE_MAX}
                  step={1}
                  value={[rebatePerRoomNight]}
                  onValueChange={value => setRebatePerRoomNight(value[0] ?? rebatePerRoomNight)}
                  aria-label="Rebate per room night"
                />
                <p className="mt-2 text-xs text-white/55">
                  Lucid’s published range is $5–$15 per room night. You choose the amount.
                </p>
              </div>

              <div className="rounded-xl border border-championship-gold/40 bg-championship-gold/10 p-5">
                <div className="mb-2 flex items-center gap-2 text-championship-gold">
                  <Hotel className="h-5 w-5" aria-hidden />
                  <p className="font-broadcast text-xs uppercase tracking-[0.18em]">
                    Illustrative lodging rebate
                  </p>
                </div>
                <p
                  className="font-broadcast text-4xl text-white"
                  data-testid="lucid-rebate-estimate"
                >
                  {money(lodgingEstimate)}
                </p>
                <p className="mt-2 text-sm text-white/70">
                  {`${boundedOutOfTown} teams × ${ROOMS_PER_TEAM} rooms × ${NIGHTS} nights × ${money(rebatePerRoomNight)} per room night. Rooms and nights are planning assumptions, not a Lucid guarantee. Paid monthly — Lucid collects at booking rather than waiting on the hotel.`}
                </p>
              </div>

              <ul className="grid gap-3 text-sm text-white/75">
                <li className="flex items-start gap-2">
                  <DollarSign
                    className="mt-0.5 h-4 w-4 shrink-0 text-championship-gold"
                    aria-hidden
                  />
                  <span>Monthly payout on bookings made through your event link.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChartLine
                    className="mt-0.5 h-4 w-4 shrink-0 text-championship-gold"
                    aria-hidden
                  />
                  <span>Stay-to-play reporting with reservation counts per team.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CalendarClock
                    className="mt-0.5 h-4 w-4 shrink-0 text-championship-gold"
                    aria-hidden
                  />
                  <span>
                    Automatic booking reminders at 60, 30, 14, and 7 days before check-in.
                  </span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
